import axios from 'axios';

// Parse time string (e.g., "30m", "5m") to minutes
const parseTimeToMinutes = (timeString: string): number => {
  const match = timeString.match(/^(\d+)([mhd])$/);
  if (!match) {
    console.warn(`Invalid time format: ${timeString}, using default 30 minutes`);
    return 30;
  } else {
    console.log('🔧 match timeString:', timeString);
  }

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case 'm':
      return value;
    case 'h':
      return value * 60;
    case 'd':
      return value * 24 * 60;
    default:
      return 30;
  }
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const REDIS_API_URL = import.meta.env.VITE_API_URL_REDIS || 'http://localhost:3001';

// Debug: Ver qué URL se está usando
console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 REDIS_API_URL:', REDIS_API_URL);
console.log('🔧 NODE_ENV:', import.meta.env.MODE);
console.log('🔧 BUILD_MODE:', import.meta.env.MODE);

// Session configuration - Alineado con JWT backend (15m)
export const SESSION_CONFIG = {
  timeoutMinutes: parseTimeToMinutes(import.meta.env.VITE_SESSION_TIMEOUT || '15m'),
  warningMinutes: parseTimeToMinutes(import.meta.env.VITE_SESSION_WARNING || '3m'),
};

// Validate session configuration
export const getValidatedSessionConfig = () => {
  const timeout = SESSION_CONFIG.timeoutMinutes;
  const warning = Math.min(SESSION_CONFIG.warningMinutes, timeout);

  if (warning > timeout) {
    console.warn(
      'Session warning time cannot be greater than timeout time. Using timeout value for warning.'
    );
  }

  return {
    timeoutMinutes: timeout,
    warningMinutes: warning,
  };
};

// Helper function to delay execution
// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line no-undef
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export Redis API URL for use in other services
export { REDIS_API_URL };

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we haven't retried yet and it's a rate limit error
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Wait for a bit before retrying (exponential backoff)
      const retryAfter = parseInt(error.response.headers['retry-after']) || 5;
      await delay(retryAfter * 1000);

      // Retry the request
      return api(originalRequest);
    }

    // Check if this is a business error from the backend (should not be treated as HTTP error)
    if (error.response?.data?.success === false && error.response?.data?.error) {
      // This is a valid business error, return it as a successful response
      return Promise.resolve({
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        config: error.config,
      });
    }

    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          // 401 = Unauthorized (token inválido/expirado)
          // SIMPLE: Si hay 401, game over. Directo al login.
          if (!window.location.pathname.includes('/login')) {
            console.warn('🚪 401 Unauthorized - Game over, redirecting to login');

            // Clear all auth data
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            // Direct redirect to login
            window.location.href = '/login';
          }
          break;
        case 403:
          // 403 = Forbidden (sin permisos suficientes)
          // NO redirigir al login, solo mostrar error
          console.error('Acceso denegado: permisos insuficientes', error.response.data);
          // El error se propagará para que el componente lo maneje
          break;
        case 404:
          // Handle not found
          console.error('Resource not found');
          break;
        case 429:
          console.error('Rate limit exceeded. Please wait before retrying.');
          break;
        default:
          console.error('API Error:', error.response.data);
      }
    }

    // Asegurarnos de que el error se propague con la información correcta
    return Promise.reject({
      ...error,
      message: error.response?.data?.error || error.message || 'An error occurred',
    });
  }
);
